/**
 * The six-gem ladder, for the admin console.
 *
 * WHY A LOCAL COPY. lib/bazaar-levels exports no gem table (a helper was
 * drafted there during this batch and REVERTED — a new lib/* export cannot be
 * typechecked from any worktree except the one the shared @workspace links
 * point at; see the worktree-trap card). The support app also has no
 * `@workspace/bazaar-levels` dependency, and adding one is a package.json
 * plus a lockfile change, which is not what a label ruling should cost. The table is
 * six rows of two fields and has not moved since #685.
 *
 * 🪤 WHAT KEEPS IT HONEST is not this comment. `gemTiers.test.ts` reads the
 * band names and boundaries out of `public/web/data.js` — Blake's TIERS array,
 * the source every other copy is also derived from — and pins this table to
 * them. A rename or a re-band that touches data.js alone fails here by name.
 * That is the same trick tutorialTierParity.test.ts already uses, for the same
 * reason: pinning a hard-coded "Emerald" would go stale the day the held
 * GEMSTONE rename lands.
 *
 * NOT to be confused with Merchant/Magnate/Mogul. Those are still computed
 * server-side (`computedTier` on the admin user payload) and still select email
 * audiences, but as of Nick's Q1 ruling (2026-09-03) nothing shows them to a
 * user, and the admin console shows the gem instead — derived from
 * `computedLevel`, which the same payload already carries.
 */

export type GemTier = "Emerald" | "Sapphire" | "Ruby" | "Amethyst" | "Gem" | "Diamond";

/** Lowest level in each band, highest band first — the order the lookup relies on. */
export const GEM_TIER_BANDS: readonly { readonly name: GemTier; readonly minLevel: number }[] = [
  { name: "Diamond", minLevel: 51 },
  { name: "Gem", minLevel: 41 },
  { name: "Amethyst", minLevel: 31 },
  { name: "Ruby", minLevel: 21 },
  { name: "Sapphire", minLevel: 11 },
  { name: "Emerald", minLevel: 1 },
];

/**
 * Chip hex per band, from data.js TIERS. Pinned to data.js by gemTiers.test.ts
 * like the names and floors, so a palette change there fails here by name.
 */
export const GEM_TIER_COLORS: Readonly<Record<GemTier, string>> = {
  Emerald: "#2FA36B",
  Sapphire: "#3E7DC4",
  Ruby: "#D86060",
  Amethyst: "#9B6FD8",
  Gem: "#C9A84C",
  Diamond: "#45B6CE",
};

/**
 * The gem band for a 1-based level.
 *
 * Anything below level 1 falls through to Emerald, matching `tierOfLvl`'s
 * `|| TIERS[0]` default in data.js: an unplaceable level reads as the bottom of
 * the ladder rather than as "undefined".
 */
export function getGemTier(level: number): GemTier {
  for (const band of GEM_TIER_BANDS) {
    if (level >= band.minLevel) return band.name;
  }
  return "Emerald";
}

/**
 * The lowest level inside a named band — the level a seller is at least at
 * when the server names their gem but withholds their points (card
 * 1219077706989065) — or null for a word that is not one of the six (the raw
 * trader_level enum, the retired Merchant/Magnate/Mogul vocabulary, a blank, a
 * non-string): no band, no tier, never a made-up Emerald.
 *
 * Case-insensitive on purpose: toPublicSeller (api-server lib/serializers.ts)
 * sends Title Case ("Emerald"), data.js's TIERS shout ("EMERALD"), and the read
 * must not depend on which spelling reached it. Pinned to data.js by
 * gemTiers.test.ts like the rest of this table.
 */
export function gemTierFloorLevel(name: unknown): number | null {
  if (typeof name !== "string") return null;
  const wanted = name.trim().toLowerCase();
  for (const band of GEM_TIER_BANDS) {
    if (band.name.toLowerCase() === wanted) return band.minLevel;
  }
  return null;
}
